export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4,
}

interface LoggerConfig {
  level: LogLevel;
  enabled: boolean;
  isDevelopment: boolean;
}

class Logger {
  private config: LoggerConfig;

  constructor() {
    this.config = {
      level: LogLevel.DEBUG,
      enabled: true,
      isDevelopment: true,
    };
  }

  private getLogLevel(): LogLevel {
    // Verifica se está em modo debug via localStorage ou variável de ambiente
    const debugMode =
      localStorage.getItem("debugMode") === "true" ||
      process.env.REACT_APP_DEBUG === "true";

    if (debugMode) {
      return LogLevel.DEBUG;
    }

    return process.env.NODE_ENV === "development"
      ? LogLevel.INFO
      : LogLevel.ERROR;
  }

  private isLoggingEnabled(): boolean {
    // Só habilita logging em desenvolvimento ou se explicitamente habilitado
    return (
      process.env.NODE_ENV === "development" ||
      localStorage.getItem("debugMode") === "true" ||
      process.env.REACT_APP_DEBUG === "true"
    );
  }

  private shouldLog(level: LogLevel): boolean {
    return this.config.enabled && level >= this.config.level;
  }

  private formatMessage(level: string, message: string, data?: any): string {
    const timestamp = new Date().toISOString().substr(11, 12);
    return `[${timestamp}] ${level}: ${message}`;
  }

  debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const formattedMessage = this.formatMessage("DEBUG", message);
      if (data !== undefined) {
        console.log(`🐛 ${formattedMessage}`, data);
      } else {
        console.log(`🐛 ${formattedMessage}`);
      }
    }
  }

  info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const formattedMessage = this.formatMessage("INFO", message);
      if (data !== undefined) {
        console.log(`ℹ️ ${formattedMessage}`, data);
      } else {
        console.log(`ℹ️ ${formattedMessage}`);
      }
    }
  }

  warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const formattedMessage = this.formatMessage("WARN", message);
      if (data !== undefined) {
        console.warn(`⚠️ ${formattedMessage}`, data);
      } else {
        console.warn(`⚠️ ${formattedMessage}`);
      }
    }
  }

  error(message: string, error?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const formattedMessage = this.formatMessage("ERROR", message);
      if (error !== undefined) {
        console.error(`❌ ${formattedMessage}`, error);
      } else {
        console.error(`❌ ${formattedMessage}`);
      }
    }
  }

  // Métodos utilitários
  enableDebug(): void {
    localStorage.setItem("debugMode", "true");
    this.config.level = LogLevel.DEBUG;
    this.config.enabled = true;
    this.info("Debug mode enabled");
  }

  disableDebug(): void {
    localStorage.removeItem("debugMode");
    this.config.level = this.getLogLevel();
    this.config.enabled = this.isLoggingEnabled();
    this.info("Debug mode disabled");
  }

  isDebugEnabled(): boolean {
    return this.config.level === LogLevel.DEBUG;
  }
}

// Instância singleton
export const logger = new Logger();

// Função de conveniência para habilitar/desabilitar debug no console do browser
(window as any).enableDebug = () => logger.enableDebug();
(window as any).disableDebug = () => logger.disableDebug();

export default logger;
